import { type RawCreateParams, type ZodRawShape, type ZodTypeAny, z } from "zod";

import { chunk } from "~/tools/chunk";
import { filter } from "~/tools/filter";
import { fromEntries } from "~/tools/from-entries";
import { isEmpty } from "~/tools/is-empty";
import { isUndefined } from "~/tools/is-undefined";
import type { NonEmptyArray } from "~/tools/non-empty-array";
import type { Object } from "~/tools/object";
import { omitBy } from "~/tools/omit-by";
import { piped } from "~/tools/piped";
import { times } from "~/tools/times";

function partial<T extends Object>(input: T) {
	const sanitized = omitBy(input, isUndefined);

	return isEmpty(sanitized) ? undefined : sanitized;
}

function optional<T>(element: T) {
	return { element };
}

/**
 * Zod.tuple does not support optional elements.
 * The custom tuple schema makes sure that the incoming tuple has the undefined placeholders for missing elements.
 */
function tuple<T extends [] | NonEmptyArray<ZodTypeAny, "writable">>(schemas: T, params?: RawCreateParams) {
	return z
		.array(z.unknown())
		.transform((elements) => [...elements, ...times(schemas.length - elements.length, () => undefined)])
		.pipe(z.tuple(schemas, params));
}

/**
 * Decode the object from a single dimensional Array with keys on even and values on odd positions.
 * Trailing odd-length chunks (malformed input) are dropped.
 */
function object<T extends ZodRawShape>(schema: T) {
	return z
		.array(z.unknown())
		.transform(
			piped(
				chunk(2),
				filter((entry) => entry.length === 2),
				fromEntries,
			),
		)
		.pipe(z.object(schema));
}

export { partial, optional, tuple, object };
